import { toValue, type MaybeRefOrGetter } from 'vue'

import { useAdaStore } from '../stores/ada'

import type { ComposedMetaData } from '@xcel-next/composer-schema'
import type { SlotPrediction } from '../stores/ada'
import type { Style } from '@unhead/vue'

// TEST: Ada's prediction is based on statistical values and often reserves way more min-height than needed. Ignoring it for now, always falling back to our own defaults. Revert by flipping this flag.
const IGNORE_ADA_SIZE_PREDICTION = true

const FALLBACK_PREDICTION: Record<string, SlotPrediction> = {
  mobile_1: { common: 250 },
  mobile_2: { common: 250 },
  mobile_3: { common: 250 },
  mobile_4: { common: 250 },
  mobile_5: { common: 250 },
  'superbanner_1-wallpaper_1': { common: 250 },
  dmofooter_1: { common: 250 },
  rectangle_1: { common: 250 },
  rectangle_2: { common: 250 },
  rectangle_3: { common: 250 },
  rectangle_4: { common: 250 },
  rectangle_5: { common: 250 },
  superbanner_2: { common: 90 },
  superbanner_3: { common: 90 },
  superbanner_4: { common: 90 },
  superbanner_5: { common: 90 }
}

const UNWANTED_PREDICTIONS = [
  'superbanner_1', 'wallpaper_1', // we use 'superbanner_1-wallpaper_1' instead (Kombislot)
  'teaser_11', 'teaser_12', 'teaser_13', // Native Teaser are scaling responsive as a normal teaser group
  'teaser_14', 'teaser_15', 'teaser_16', 'teaser_17', 'teaser_18', 'teaser_19',
  'teaser_20', 'teaser_21', 'teaser_22'
]

export function useAdaSizePredictionCss (metaData: MaybeRefOrGetter<ComposedMetaData | undefined>) {
  const adaStore = useAdaStore()

  function ensureAboveTheFoldMinHeight (slotName: string, prediction: number): number {
    // for above the fold slots we set the max height for all pages to ensure that there are no layout shifts
    if (slotName === 'superbanner_1-wallpaper_1' || slotName === 'mobile_1') {
      return FALLBACK_PREDICTION[slotName].common!
    }
    return prediction < 1000 ? prediction : 0
  }

  function getPredictionZone (): string {
    const adZone = toValue(metaData)?.adAAdZone
    return !IGNORE_ADA_SIZE_PREDICTION && adaStore.adSizePrediction?.adZones?.includes(adZone ?? '') ? adZone ?? 'FALLBACK' : 'FALLBACK'
  }

  function getPredictedAdZonesString (): string | undefined {
    return IGNORE_ADA_SIZE_PREDICTION ? undefined : adaStore.adSizePrediction?.adZones?.join(', ')
  }

  /*
   * Returns a string of CSS styles based on the adSizePrediction object.
   * -----
   * Original Ada adSizePrediction is fetched, reduced, combined and put into redis via cron-jobs/ad-size-prediction-fetcher.
   * The middlewares/ad-size-prediction greps the latest data from redis and puts it into the request object.
   * At least the mixins/default-app-init.js uses the data and puts it into the Ada Store from which we access it here.
   * -----
   */
  function getAdSizePredictionStyles (): string {
    const adSizePrediction = adaStore.adSizePrediction
    const adZone = toValue(metaData)?.adAAdZone

    const rawPrediction = !IGNORE_ADA_SIZE_PREDICTION && adSizePrediction?.adZones?.includes(adZone ?? '')
      ? adSizePrediction[adZone ?? ''] as Record<string, SlotPrediction>
      : FALLBACK_PREDICTION

    const prediction = { ...rawPrediction }
    UNWANTED_PREDICTIONS.forEach(slot => delete prediction[slot])

    let styles = ''
    const predictedMinHeights: Record<string, number> = {}
    for (const [key, value] of Object.entries(prediction)) {
      if (value?.common != null && value.common >= 0) { // prediction can be 0, check explicitly
        const height = ensureAboveTheFoldMinHeight(key, value.common)
        styles += `.bx-ada--${key} { min-height: ${height}px; }\n`
        predictedMinHeights[key] = height
      } else {
        styles += `.bx-ada--${key} { min-height: ${ensureAboveTheFoldMinHeight(key, value.mobile ?? 0)}px; }\n`
        styles += `@media (min-width: 768px) { .bx-ada--${key} { min-height: ${ensureAboveTheFoldMinHeight(key, value.desktop ?? 0)}px; } }\n`
      }
    }
    adaStore.setPredictedMinHeights(predictedMinHeights)
    return process.env.NODE_ENV !== 'production' ? styles : styles.replace(/[ \s]/g, '')
  }

  function getAdSizePredictionStyleData (): Style {
    const adZone = toValue(metaData)?.adAAdZone
    const predictedAdZones = getPredictedAdZonesString()
    const generationTime = IGNORE_ADA_SIZE_PREDICTION ? undefined : adaStore.adSizePrediction?.generationTime

    return {
      // no 'type' field here - <style type="text/css"> is obsolete in HTML5 and not part of unhead's Style type
      'data-ada-prediction-zone': getPredictionZone(),
      // omit entirely rather than falling back to '' - an empty string would still render the attribute
      ...(adZone ? { 'data-ada-ad-zone': adZone } : {}),
      ...(predictedAdZones ? { 'data-ada-predicted-ad-zones': predictedAdZones } : {}),
      ...(generationTime ? { 'data-ada-generation-time': generationTime } : {}),
      key: 'ad-size-prediction-css',
      innerHTML: getAdSizePredictionStyles()
    }
  }

  return {
    getAdSizePredictionStyleData,
    getAdSizePredictionStyles,
    getPredictionZone,
    getPredictedAdZonesString
  }
}
